/**
 * Responsibility: Implement autonomy behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { GameState, Companion } from "@anyquest/shared-core"
import { CombatStatus } from "@anyquest/shared-core"
import { router } from '../ai/router'
import { formatVitalValue, vitalPercent } from './vitals'
import { isCompanionPresent } from './companions'
// ============================================================
// AUTONOMY.TS — Companion autonomy engine
// Driven by companion motives + feelings + current game state.
// Produces natural-language action strings that feed back
// into the main handleAction() loop (isCompanionAction=true).
// ============================================================

const AUTONOMY_SYSTEM = `You are the autonomy engine for an RPG party.
Decide which companions, if any, should act this turn based on their motives, feelings, vitals, and the current scene.
Output strict JSON containing an array of actions:
{
  "actions": [
    {
      "name": string,    // Companion's name
      "action": string,  // Natural-language action sentence (e.g. "Attack the goblin")
      "reason": string   // ≤10-word internal rationale
    }
  ]
}
Rules:
- It is valid for zero companions to act. Return an empty array when no companion action would add clear value.
- Do not force filler actions just to make companions seem alive.
- Outside combat, prefer at most one meaningful companion action unless the scene has urgent pressure.
- If a companion already acted recently and nothing changed, let them stay quiet.
- Prioritise high-priority motives.
- If a companion's vitality < 30, prefer defensive or healing actions.
- If a companion has a strong positive feeling toward Player (intensity >= 7), prefer protective actions.
- If combat is active, lean toward combat actions unless motives say otherwise.
- Item usage is allowed: if useful, output actions like "Use Smoke Bomb on Grizzy" or "Use Healing Draught on Pico".
- Keep the action concise — it will be spoken as intent, like a player command.`
const GENERIC_IDLE_PATTERNS = [
  /\bstands?\s+watch/i,
  /\bwatches?\s+the\s+situation/i,
  /\bkeeps?\s+watch\b/i,
  /\bwaits?\b/i,
  /\bholds?\s+position\b/i,
  /\bstays?\s+alert\b/i,
]

function normalizeActionText(value: string): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/<[^>]+>/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function countRecentCompanionActions(state: GameState, companionName: string, lookback = 8): number {
  const marker = `**${String(companionName ?? '').trim()}**:`
  if (!marker || marker === '****:') return 0
  return (state.history ?? [])
    .slice(-lookback)
    .filter(entry => String(entry?.text ?? '').startsWith(marker))
    .length
}

function hasUrgentCompanionPressure(companion: Companion): boolean {
  const vitals = companion.vitals
  if (!vitals) return false
  if (vitalPercent(vitals, 'vitality') < 45 || vitalPercent(vitals, 'spirit') < 35 || vitalPercent(vitals, 'essence') < 30) return true
  if ((companion.motives ?? []).some(motive => ['high', 'critical'].includes(String(motive?.priority ?? '').toLowerCase()))) return true
  if ((companion.feelings ?? []).some(feeling => Number(feeling?.intensity ?? 0) >= 8)) return true
  return false
}

export function shouldCompanionActThisTurn(state: GameState, companion: Companion): boolean {
  if (!companion?.name) return false
  if (!isCompanionPresent(companion, state.player.location)) return false
  if (state.combat?.status === CombatStatus.active) return true
  if (hasUrgentCompanionPressure(companion)) return true

  const recentOwnActs = countRecentCompanionActions(state, companion.name)
  if (recentOwnActs >= 2) return false

  const roomNpcCount = state.currentRoom?.npcs?.length ?? 0
  const roomItemCount = state.currentRoom?.items?.length ?? 0
  const roomFlagCount = Object.keys(state.currentRoom?.flags ?? {}).length + (state.currentRoom?.dormantFlags?.length ?? 0)
  const scenePressure = roomNpcCount + Math.min(roomItemCount, 1) + Math.min(roomFlagCount, 1)

  if (recentOwnActs >= 1 && scenePressure <= 1) return false
  return scenePressure >= 2
}

function actionLooksLikeGenericIdle(action: string): boolean {
  const text = String(action ?? '').trim()
  return GENERIC_IDLE_PATTERNS.some(pattern => pattern.test(text))
}

function filterAutonomyResults(
  state: GameState,
  companions: Companion[],
  actions: Array<{ name?: string; action?: string }>,
): { name: string; action: string }[] {
  const byName = new Map(companions.map(companion => [String(companion.name ?? '').trim().toLowerCase(), companion]))
  const filtered: { name: string; action: string }[] = []

  for (const raw of actions ?? []) {
    const nameKey = String(raw?.name ?? '').trim().toLowerCase()
    const companion = byName.get(nameKey)
    const action = String(raw?.action ?? '').trim()
    if (!companion || !action) continue
    if (!shouldCompanionActThisTurn(state, companion) && actionLooksLikeGenericIdle(action)) continue
    if (normalizeActionText(action) === normalizeActionText(companion.name)) continue
    filtered.push({ name: companion.name, action })
  }

  if (state.combat?.status === CombatStatus.active) return filtered

  const urgentCount = companions.filter(companion => hasUrgentCompanionPressure(companion)).length
  return filtered.slice(0, Math.max(urgentCount, 1))
}

export async function getCompanionActions(state: GameState, companions: Companion[]): Promise<{ name: string, action: string }[]> {
  if (!companions || companions.length === 0) return []
  const eligibleCompanions = companions.filter(companion => shouldCompanionActThisTurn(state, companion))
  if (!eligibleCompanions.length) return []

  const combat = state.combat?.status === CombatStatus.active
    ? `\nCombat: Round ${state.combat.round}
Allies: ${[state.player.identity, ...((state.combat.allies ?? []).filter(Boolean))].join(', ')}
Foes: ${(state.combat.foes ?? [{ name: state.combat.npc.name, vitality: state.combat.npcVitality }]).map(f => `${f.name}(${f.vitality}%)`).join(', ')}
Active target: ${state.combat.activeTargetName ?? state.combat.npc.name}`
    : ''
  const recentCompanionActions = eligibleCompanions.map(companion => {
    const recent = (state.history ?? [])
      .slice(-8)
      .filter(entry => String(entry?.text ?? '').startsWith(`**${companion.name}**:`))
      .slice(-2)
      .map(entry => String(entry.text ?? '').split('\n\n')[0].replace(/\*\*/g, '').trim())
      .join(' | ')
    return `${companion.name}: ${recent || 'none in recent turns'}`
  }).join('\n')

  const companionsContext = eligibleCompanions.map(c => {
    return `Companion: ${c.name} (${c.identity})
Bio: ${c.bio}
Vitals: VIT:${formatVitalValue(c.vitals, 'vitality')} SPR:${formatVitalValue(c.vitals, 'spirit')} ESS:${formatVitalValue(c.vitals, 'essence')}
Abilities: ${c.abilities.map(a => a.name).join(', ') || 'none'}
Motives: ${JSON.stringify(c.motives)}
Feelings: ${JSON.stringify(c.feelings)}
Inventory: ${c.inventory.map(i => i.equipped ? `${i.name}[E]` : i.name).join(', ') || 'none'}
Active effects: ${(c.modifiers ? Object.values(c.modifiers).flat().map(m => m?.label).filter(Boolean).join(', ') : '') || 'none'}`
  }).join('\n\n')
  const prompt = `Current Scene Context:
Room: ${state.currentRoom.name} — ${state.currentRoom.desc.slice(0, 80)}
NPCs present: ${state.currentRoom.npcs.map(n => n.name).join(', ') || 'none'}
Items present: ${state.currentRoom.items.join(', ') || 'none'}
Player vitals: VIT:${formatVitalValue(state.player.vitals, 'vitality')} SPR:${formatVitalValue(state.player.vitals, 'spirit')} ESS:${formatVitalValue(state.player.vitals, 'essence')}${combat}
Recent companion actions:
${recentCompanionActions}

Companions:
${companionsContext}`
  try {
    const result = await router.route<{ actions: { name: string; action: string; reason: string }[] }>({
      jobType: 'logic_forge', // cheap structured task
      systemPrompt: AUTONOMY_SYSTEM,
      userPrompt: prompt,
    })
    return filterAutonomyResults(state, eligibleCompanions, result.actions ?? [])
  } catch {
    return []
  }
}
